<template>
    <AppHead :title="recording.title" />

    <section class="px-4 xl:px-0 pb-16 pt-6 md:pt-0">
        <SectionHeading :title="recording.title">
            <div v-if="working" class="flex space-x-2 py-2 md:py-3">
                <svg class="animate-spin h-6 w-6 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <div class="font-medium text-md">Processing</div>
            </div>
            <div v-else>
                <div v-if="!editing" class="flex space-x-2 items-center">
                    <a :href="url" download="recording.title" target="_blank">
                        <Button icon="CloudDownloadIcon" label="Download" type="secondary" />
                    </a>
                    <Button :href="route('episodes.create', {recording: recording.uuid})" label="Publish" />
                </div>
                <div v-else class="flex space-x-2 items-center">
                    <Button @click="reset" label="Reset" type="secondary" />
                    <Button @click="save" label="Save" />
                </div>
            </div>
        </SectionHeading>
    </section>

    <section class="px-4 xl:px-0 pb-16 pt-6 md:pt-0">
        <audio ref="player" :src="url" controls class="w-full hidden" />
        <div class="w-full flex items-center justify-center">
            <div>
                <div @click="play" v-if="!playing" class="bg-regal-blue-700 hover:bg-regal-blue-800 text-white shrink-0 flex items-center justify-center w-16 h-16 rounded-full transition duration-100 ease-out cursor-pointer">
                    <svg class="fill-current w-7 h-7 -mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M3 22v-20l18 10-18 10z"/></svg>
                </div>
                <div @click="pause" v-if="playing" class="bg-regal-blue-700 hover:bg-regal-blue-800 text-white shrink-0 flex items-center justify-center w-16 h-16 rounded-full transition duration-100 ease-out cursor-pointer">
                    <svg class="fill-current w-12 h-12" viewBox="0 0 512 512"> <g> </g> <path d="M162.642 148.337h56.034v215.317h-56.034v-215.316z"/> <path d="M293.356 148.337h56.002v215.317h-56.002v-215.316z" /></svg>
                </div>
            </div>
        </div>
    </section>

    <section class="px-4 xl:px-0 pb-16 pt-6 md:pt-0">
        <div @click.stop="seek" ref="clipContainerEl" class="w-full h-32 relative">
            <div v-show="!bounds.find(el => el.dragging)" class="w-[1px] h-full bg-red-500 absolute z-30" :style="'left:' + playheadPosition + 'px'"></div>
            <div v-if="!recording.trimmedUrl" ref="leftOverlay" class="absolute left-0 backdrop-saturate-0 z-20 h-full backdrop-brightness-[3.25] rounded-l-md" :style="'width: ' + leftOverlayWidth + 'px'"></div>
            <div v-if="!recording.trimmedUrl" ref="rightOverlay" class="absolute right-0 backdrop-saturate-0 z-20 h-full backdrop-brightness-[3.25] rounded-r-md" :style="'width: ' + rightOverlayWidth + 'px'"></div>
            <div v-if="!recording.trimmedUrl" @pointerdown="handlePointerDown($event, 'pos')" ref="clipSectionEl" class="absolute h-32 box-border border-2 rounded-md border-regal-blue-700 border-b-regal-blue-700 z-20" :style="'width: ' + clipSection.width + 'px; left: ' + clipSection.left + 'px'">
                <div class="absolute left-0 top-0 bg-regal-blue-700 w-3 h-0.5 -translate-y-[2px] -translate-x-1" />
                <div class="absolute right-0 top-0 bg-regal-blue-700 w-3 h-0.5 -translate-y-[2px] translate-x-1" />
                <div class="absolute right-0 bottom-0 bg-regal-blue-700 w-3 h-0.5 translate-y-[2px] translate-x-1" />
                <div class="absolute left-0 bottom-0 bg-regal-blue-700 w-3 h-0.5 translate-y-[2px] -translate-x-1" />
                <div :ref="bound.position" v-for="bound in bounds" @pointerdown="handlePointerDown($event, bound)" :class="[bound.position == 'left' ? 'left-0 -ml-4 rounded-l-md' : 'right-0 -mr-4 rounded-r-md', 'absolute h-[128px] -translate-y-[2px] w-4 bg-regal-blue-700 flex items-center justify-center space-x-0.5 group cursor-ew-resize']">
                    <div class="w-[1px] h-4 bg-regal-blue-400 rounded-full" />
                    <div class="w-[1px] h-4 bg-regal-blue-400 rounded-full" />
                </div>
            </div>
            <div @click.prevent="" class="absolute w-full h-full z-10 rounded-md overflow-hidden bg-white">
                <div @click.prevent="" id="waveform" class="w-full h-full -ml-[1px]">
                    
                </div>
            </div>
        </div>
    </section>



</template>

<script setup>
import SectionHeading from '@/Shared/SectionHeading'
import { defineProps, ref, onMounted, nextTick } from 'vue'
import WaveSurfer from 'wavesurfer.js'
import Button from '@/Shared/Button'
import axios from 'axios'

const props = defineProps(['recording'])
const left = ref(null)
const right = ref(null)
const clipSectionEl = ref(null)
const clipContainerEl = ref(null)
const prevClientX = ref(null)
const player = ref(null)
const duration = ref(props.recording.duration)
const playing = ref(false)
const currentStep = ref(null)
const playheadPosition = ref(0)
const currentTime = ref(0)
const wavesurfer = ref(null)
const leftOverlay = ref(null)
const rightOverlay = ref(null)
const leftOverlayWidth = ref(0)
const rightOverlayWidth = ref(0)
const editing = ref(false)
const working = ref(props.recording.working)
const url = ref(props.recording.trimmedUrl ? props.recording.trimmedUrl : props.recording.previewUrl)

const trim = ref({
    start: 0,
    duration: props.recording.duration,
})

const bounds = ref([
    {
        position: 'left',
        dragging: false,
    },
    {
        position: 'right',
        dragging: false,
    }
])

const clipContainer = ref(null)

const clipSection = ref({
    width: 0,
    left: 0,
    offsetX: 0,
    maxLeft: 0,
    lastWidth: 0,
})

onMounted(() => {
    wavesurfer.value = WaveSurfer.create({
        container: '#waveform',
        barWidth: 3,
        barHeight: 1,
        barGap: null,
        waveColor: '#433edb',
        interact: false,
        barRadius: 3,
        backend: 'MediaElementWebAudio',
        xhr: {
            cache: "no-cache",
            mode: "cors",
            method: "GET",
            credentials: "include",
            headers: [
                { key: "cache-control", value: "no-cache, no-store, max-age=0" },
                { key: "pragma", value: "no-cache" }
            ]
        }
    })

    wavesurfer.value.load(url.value)

    clipSection.value.width = clipContainerEl.value.getBoundingClientRect().width

    player.value.onloadedmetadata = () => {
        duration.value = player.value.duration
        trim.value.duration = duration.value
    }
    player.value.onplay = () => {
        playing.value = true
        currentStep.value = requestAnimationFrame(step)
    }
    player.value.onpause = () => {
        playing.value = false
        cancelAnimationFrame(currentStep.value)
    }
    player.value.onended = () => {
        playing.value = false
        playheadPosition.value = 0
        cancelAnimationFrame(currentStep.value)
        console.log('end')
    }
    player.value.ontimeupdate = () => {
    }
})

const step = () => {
    if (player.value.currentTime > trim.value.start + trim.value.duration) {
        pause()
        player.value.currentTime = trim.value.start ?? 0
        updatePlayheadPosition(player.value.currentTime)
        cancelAnimationFrame(currentStep.value)
        return
    }
    updatePlayheadPosition(player.value.currentTime)
    currentStep.value = requestAnimationFrame(step)
}

const play = () => {
    if (player.value.currentTime >= trim.value.start && player.value.currentTime <= trim.value.start + trim.value.duration) {
        player.value.play()
        return
    }
    player.value.currentTime = trim.value.start
    player.value.play()
}

const pause = () => {
    player.value.pause()
}

const seek = (e) => {
    var pos = (e.clientX - e.currentTarget.getBoundingClientRect().left) / e.currentTarget.getBoundingClientRect().width
    if (e.clientX - e.currentTarget.getBoundingClientRect().left < clipSection.value.left) {
        updateLeftBound(e.clientX)
    }
    if (e.clientX - e.currentTarget.getBoundingClientRect().left > clipSection.value.left + clipSection.value.width) {
        updateRightBound(e.clientX)
    }
    player.value.currentTime = duration.value * pos
    updatePlayheadPosition(player.value.currentTime)
}

const handlePointerDown = (e, bound) => {
    editing.value = true
    if (bound !== 'pos') {
        const el = e.target
        bounds.value.forEach(el => {
            el.dragging = el == bound ? true : false
        })
    }
    clipSection.value.offsetX = e.offsetX + clipContainerEl.value.getBoundingClientRect().left
    clipSection.value.maxLeft = clipContainerEl.value.getBoundingClientRect().width - clipSectionEl.value.getBoundingClientRect().width
    clipSection.value.lastWidth = clipSectionEl.value.getBoundingClientRect().right - clipContainerEl.value.getBoundingClientRect().left
    clipSection.value.lastRight = clipSectionEl.value.getBoundingClientRect().right - 59

    if (playing.value) {
        pause()
    }

    document.addEventListener('pointermove', handlePointerMove, { passive: true })
    document.addEventListener('pointerup', handlePointerUp, { passive: true })
}

const handlePointerMove = async (e) => {
    // Prevent double firing
    if (prevClientX.value == e.clientX) {
        return
    }

    const draggedEl = bounds.value.find(el => el.dragging)

    if (!draggedEl) {
        let minBound = e.target.parentNode.offsetLeft
        let maxBound = e.target.parentNode.offsetLeft + e.target.parentNode.offsetWidth - e.target.offsetWidth

        if (e.clientX - clipSection.value.offsetX < 0) {
            clipSection.value.left = 0
        } else if ((e.clientX - clipSection.value.offsetX + clipContainerEl.value.getBoundingClientRect().left) + clipSectionEl.value.getBoundingClientRect().width > clipContainerEl.value.getBoundingClientRect().right) {
            clipSection.value.left = clipSection.value.maxLeft
        } else {
            clipSection.value.left = e.clientX - clipSection.value.offsetX
        }  
    }

    if (draggedEl?.position == 'right') {
        updateRightBound(e.clientX)
    }

    if (draggedEl?.position == 'left') {
        updateLeftBound(e.clientX)
    }

    leftOverlayWidth.value = clipSectionEl.value.getBoundingClientRect().left - clipContainerEl.value.getBoundingClientRect().left
    rightOverlayWidth.value = clipContainerEl.value.getBoundingClientRect().right - clipSectionEl.value.getBoundingClientRect().right

    prevClientX.value = e.clientX
}

const updateLeftBound = (posX) => {
        if (posX < clipContainerEl.value.getBoundingClientRect().left) {
            clipSection.value.left = 0
            clipSection.value.width = clipSection.value.lastWidth
        } else if(posX > clipSectionEl.value.getBoundingClientRect().right - 24) {
            clipSection.value.width = 24
            clipSection.value.left = clipSection.value.lastRight
        } else {
            clipSection.value.left = posX - clipContainerEl.value.getBoundingClientRect().left
            clipSection.value.width = clipSectionEl.value.getBoundingClientRect().right - posX
        }
}

const updateRightBound = (posX) => {
        if (posX > clipContainerEl.value.getBoundingClientRect().right) {
            clipSection.value.width = clipContainerEl.value.getBoundingClientRect().width - clipSection.value.left
        } else if(posX < clipSectionEl.value.getBoundingClientRect().left + 24) {
            clipSection.value.width = 24
        } else {
            clipSection.value.width = posX - clipSectionEl.value.getBoundingClientRect().left
        }
}

const handlePointerUp = (e) => {
    trim.value.start = Math.round(((clipSectionEl.value.getBoundingClientRect().left - clipContainerEl.value.getBoundingClientRect().left) / clipContainerEl.value.getBoundingClientRect().width) * duration.value * 100) / 100

    trim.value.duration = Math.round((clipSectionEl.value.getBoundingClientRect().width / clipContainerEl.value.getBoundingClientRect().width) * duration.value * 100) / 100
    bounds.value.forEach(el => el.dragging = false)
    updatePlayheadPosition(trim.value.start)
    document.removeEventListener('pointermove', handlePointerMove)
    document.removeEventListener('pointerup', handlePointerUp)
}

const updatePlayheadPosition = (sec) => {
    playheadPosition.value = (sec / duration.value) * clipContainerEl.value.getBoundingClientRect().width
}

const reset = () => {
    trim.value.start = 0
    trim.value.duration = props.recording.duration
    clipSection.value.width = clipContainerEl.value.getBoundingClientRect().width
    clipSection.value.left = 0
    clipSection.value.offsetX = 0
    clipSection.value.maxLeft = 0
    clipSection.value.lastWidth = 0
    playheadPosition.value = 0
    leftOverlayWidth.value = 0
    rightOverlayWidth.value = 0
    editing.value = false
}

const save = () => {
    axios.patch(route('recordings.update', props.recording.uuid), {
        trim: trim.value
    }).then((res) => {
        editing.value = false
        working.value = true
    })
}

</script>

<script>
import Layout from '@/Shared/Layout'

export default {
    layout: Layout,
}
</script>